import { Injectable } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { get } from 'lodash-es';
import { filter } from 'rxjs/operators';

import { User } from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandler {
  private readonly httpResponseStatus = {
    noConnection: 0,
    internalServerError: 500,
  };

  constructor(
    private readonly toastr: ToastrService,
    private readonly i18nService: TmpI18NService,
    private readonly userService: UserService,
  ) {
    this.userService.currentUser.pipe(filter((user: User) => !!(user && user.id))).subscribe((user) => {
      Bugsnag.setUser(user.id, user.email, user.fullName);
    });
  }

  handle(errorObject, context?, title?, opts?): void {
    this.notifyBugsnag(errorObject, context);
    const { status, error, hideToastr, toastKey } = errorObject;

    if (hideToastr) {
      return;
    }

    if (toastKey) {
      this.toastr.errorByKey(toastKey);
      return;
    }

    const message =
      this.getStatusMessage(status) ||
      this.extractErrorMessage(error) ||
      this.i18nService.getMessage('generalErr');

    this.toastr.error(message, title, opts);
  }

  handleForDebug(errorObject, context?): void {
    this.notifyBugsnag(errorObject, context);
  }

  private notifyBugsnag(errorObject, context) {
    if (errorObject) {
      // TODO: test how this logs the meta data (we might need to adjust/manipulate the context object)
      Bugsnag.notify(errorObject, (event) => event.addMetadata('metadata', context));
      // eslint-disable-next-line no-console
      console.error(errorObject);
    }
  }

  private getStatusMessage(status: number): string {
    if (status === this.httpResponseStatus.noConnection) {
      return this.i18nService.getMessage('noConnectionErr');
    }

    if (status === this.httpResponseStatus.internalServerError) {
      return this.i18nService.getMessage('generalErr');
    }

    return null;
  }

  private extractErrorMessage(error) {
    if (!error) {
      return null;
    }

    return get(error, 'message.message') || error.message || error;
  }
}
